import React, { useEffect } from "react";
import { Box } from "@mui/material";
import {
  AlertSpg,
  BorderGreyBox,
  handleSubmitValidationFail,
  isNotEmpty,
  useReactHookForm,
} from "@duosoftbg/nacid-components";
import { useTranslation } from "react-i18next";
import { useStore } from "react-redux";
import { FormProvider } from "react-hook-form";

import { IValARUniversitySearch, ivalARUniversitySearch } from "../../../../../../config/initialFormValues";
import { vARUniversitySearchTable } from "../../../../../../yup/schemas";
import useSearchTableControl from "../../../../../../hooks/registers/search/useSearchTableControl";
import { searchAcademicRecognitionUniversities } from "../../../../../../axios/api/services";
import SearchFilters from "../../base/filters/SearchFilters";
import ApplicantFilter from "../../base/filters/definition/ApplicantFilter";
import ForeignUniversityFilter from "../../base/filters/definition/ForeignUniversityFilter";
import RecognizedSpecialityFilter from "../../base/filters/definition/RecognizedSpecialityFilter";
import RSearchAcademicRecognitionUniversitiesList from "./RSearchAcademicRecognitionUniversitiesList";
import {
  SEARCH_FILTERS_GROUP,
  SYNC_GROUPS_CONFIG,
} from "../../../../../../config/registers/search/filters/groupsConfig";
import ARUniversityStatusFilter from "../../base/filters/definition/ARUniversityStatusFilter";
import UniversityCountryNameFilter from "../../base/filters/definition/UniversityCountryNameFilter";
import AcquiredSpecialityFilter from "../../base/filters/definition/AcquiredSpecialityFilter";
import UniversityFilter from "../../base/filters/definition/UniversityFilter";
import SimpleEduLevelFilter from "../../base/filters/definition/SimpleEduLevelFilter";
import RecognizedEduLevelFilter from "../../base/filters/definition/RecognizedEduLevelFilter";
import RecognizeOrRejectOrderFilter from "../../base/filters/definition/RecognizeOrRejectOrderFilter";
import { RootState } from "../../../../../../store/redux/store";

const RSearchAcademicRecognitionUniversities = () => {
  const { t } = useTranslation();
  const store = useStore();
  const group = SEARCH_FILTERS_GROUP.ACADEMIC_RECOGNITION_UNIVERSITIES;

  const { methods } = useReactHookForm<IValARUniversitySearch>({
    defaultValues: ivalARUniversitySearch,
    validationSchema: vARUniversitySearchTable,
  });

  useEffect(() => {
    const filtersData = (store.getState() as RootState).SearchData.searchTable[group].filtersData;
    if (isNotEmpty(filtersData)) {
      methods.reset(filtersData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const control = useSearchTableControl({
    group,
    methods,
    initialValues: ivalARUniversitySearch,
    filterData: searchAcademicRecognitionUniversities,
    callAfterResetFilters: true,
    syncGroups: {
      groups: [SEARCH_FILTERS_GROUP.ACADEMIC_RECOGNITION_NACID],
      config: SYNC_GROUPS_CONFIG.ARSyncConfig,
    },
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(
          () => control.handleSubmitFilters(),
          () => handleSubmitValidationFail(methods.formState, t, undefined),
        )}
      >
        <Box>
          <BorderGreyBox>
            <SearchFilters
              isSubmitBtnDisabled={control.isSubmitBtnDisabled}
              isResetBtnDisabled={control.isResetBtnDisabled}
              handleResetFilters={control.handleResetFilters}
            >
              <ApplicantFilter label={"l.diplomaOwner"} />
              <RecognizedSpecialityFilter />
              <ForeignUniversityFilter />
              <ARUniversityStatusFilter />
              <UniversityCountryNameFilter />
              <AcquiredSpecialityFilter />
              <UniversityFilter />
              <SimpleEduLevelFilter />
              <RecognizedEduLevelFilter md={6} />
              <RecognizeOrRejectOrderFilter md={6} />
              <AlertSpg mt={5} ml={4} severity={"info"}>
                <span dangerouslySetInnerHTML={{ __html: t("m.info.ar.uni") }} />
              </AlertSpg>
            </SearchFilters>
          </BorderGreyBox>
          {control.showList && (
            <RSearchAcademicRecognitionUniversitiesList
              records={control.records}
              total={control.total}
              isLoading={control.isLoading}
              onPageOrOrderChange={control.handlePageOrOrderChange}
              blockTable={control.blockTable}
              group={group}
              viewURL={"/academic-recognitions/view?id="}
            />
          )}
        </Box>
      </form>
    </FormProvider>
  );
};

export default RSearchAcademicRecognitionUniversities;
